package co.uk.paxes.pinpoint.web.ads

import kotlinx.browser.window
import kotlin.js.JsExport
import kotlin.js.JsName
import co.uk.paxes.pinpoint.web.AdManager
import co.uk.paxes.pinpoint.web.platform.PlatformEnvironment

/**
 * Manages interstitial ad display timing for AI games and multiplayer.
 * 
 * Shows interstitial ads after a configurable number of hands have been played.
 * - PlayGama: Uses bridge.advertisement.showInterstitial()
 * - Facebook: Uses Facebook Audience Network
 * - Default interval is 5 hands.
 */
@JsExport
object InterstitialAdManager {
    
    // Configuration
    private var adInterval: Int = 5  // Show ad every 5 hands by default
    
    // State tracking
    private var handsPlayed: Int = 0
    
    /**
     * Set the interval for showing interstitial ads.
     * 
     * @param interval Number of hands between ads (must be > 0)
     */
    @JsName("setAdInterval")
    fun setAdInterval(interval: Int) {
        if (interval > 0) {
            adInterval = interval
            console.log("📺 InterstitialAdManager: Ad interval set to $interval hands")
        } else {
            console.warn("⚠️ InterstitialAdManager: Invalid interval $interval, keeping current value $adInterval")
        }
    }
    
    /**
     * Get the current ad interval.
     */
    @JsName("getAdInterval")
    fun getAdInterval(): Int = adInterval
    
    /**
     * Get the number of hands played since last ad.
     */
    @JsName("getHandsPlayed")
    fun getHandsPlayed(): Int = handsPlayed
    
    /**
     * Called when a hand is completed.
     * Checks if it's time to show an interstitial ad.
     */
    @JsName("onHandComplete")
    fun onHandComplete() {
        handsPlayed++
        console.log("📊 InterstitialAdManager: Hand completed ($handsPlayed/$adInterval)")
        
        // Check if it's time to show an ad
        if (handsPlayed >= adInterval) {
            console.log("📺 InterstitialAdManager: Time to show interstitial ad!")
            showInterstitialAd()
        }
    }
    
    /**
     * Show an interstitial ad based on platform.
     */
    private fun showInterstitialAd() {
        try {
            val env = PlatformEnvironment.getEnvironment()
            
            // PlayGama: Use bridge ads
            if (env == PlatformEnvironment.Environment.PLAYGAMA) {
                console.log("📺 InterstitialAdManager: Showing PlayGama interstitial ad")
                val adShown = AdManager.showInterstitialAd("interstitial_hand_interval")
                if (adShown) {
                    console.log("✅ InterstitialAdManager: PlayGama ad triggered, resetting counter")
                    handsPlayed = 0
                } else {
                    console.log("⚠️ InterstitialAdManager: PlayGama ad not shown (throttled or unavailable), resetting anyway")
                    handsPlayed = 0
                }
                return
            }
            
            // Facebook/Other: Use Facebook Ads
            console.log("📺 InterstitialAdManager: Showing Facebook interstitial ad")
            FacebookAdService.showInterstitialAd(
                placementID = "interstitial_hand_interval",
                onClosed = {
                    console.log("✅ InterstitialAdManager: Ad closed, resetting counter")
                    handsPlayed = 0
                },
                onFailed = {
                    console.log("❌ InterstitialAdManager: Ad failed, resetting counter anyway")
                    handsPlayed = 0
                }
            )
        } catch (e: Exception) {
            console.warn("⚠️ InterstitialAdManager: Error showing ad: ${e.message}")
            handsPlayed = 0
        }
    }
    
    /**
     * Reset the hands played counter.
     * Useful when starting a new game session.
     */
    @JsName("reset")
    fun reset() {
        handsPlayed = 0
        console.log("🔄 InterstitialAdManager: Counter reset")
    }
}
